import React, { useState, useEffect, useCallback } from 'react';
import './common.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';

// ─── Constants ────────────────────────────────────────────────────────────────
const PROJECTIONS = [
  { id: 'overview',   label: '🏠 Overview',     title: 'Activity Portfolio Overview' },
  { id: 'activity',   label: '🎮 By Activity',   title: 'Performance by Activity' },
  { id: 'level',      label: '🎓 By Level',      title: 'Performance by Level' },
  { id: 'timeline',   label: '📅 Timeline',      title: 'Activity Timeline' },
];

const LEVEL_ORDER   = ['beginner','elementary','intermediate','advanced','expert','master'];
const LEVEL_COLORS  = {
  beginner:     '#a8e6cf',
  elementary:   '#fddb92',
  intermediate: '#a29bfe',
  advanced:     '#fd79a8',
  expert:       '#e17055',
  master:       '#6c5ce7',
};
const ACTIVITY_PALETTE = [
  '#667eea','#f093fb','#4facfe','#43e97b','#fa709a','#fee140',
  '#a18cd1','#fda085','#84fab0','#f6d365','#89f7fe','#fccb90',
];
const STATUS_COLOR = { completed: '#26de81', incomplete: '#fd9644', error: '#fc5c65' };

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmt(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function fmtDate(iso) {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function avg(arr) { return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0; }
function best(arr) { return arr.length ? Math.max(...arr) : 0; }

// ─── Sub-components ───────────────────────────────────────────────────────────

/** A single KPI card */
function KpiCard({ icon, label, value, sub, bg }) {
  return (
    <div style={{
      background: bg || 'linear-gradient(135deg,#667eea,#764ba2)',
      borderRadius: 20, padding: '24px 28px', color: '#fff',
      minWidth: 160, flex: '1 1 160px',
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    }}>
      <div style={{ fontSize: '2.2rem', marginBottom: 6 }}>{icon}</div>
      <div style={{ fontSize: '2rem', fontWeight: 900, lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: '0.85rem', opacity: 0.85, marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: '0.78rem', opacity: 0.7, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

/** Horizontal score bar */
function ScoreBar({ pct, color, height = 14 }) {
  return (
    <div style={{ background: '#e9ecef', borderRadius: 99, overflow: 'hidden', height }}>
      <div style={{
        width: `${Math.min(100, Math.max(0, pct))}%`,
        height: '100%',
        background: color || '#667eea',
        borderRadius: 99,
        transition: 'width 0.6s ease',
      }} />
    </div>
  );
}

/** Mini sparkline (SVG) for last N scores */
function Sparkline({ scores, color = '#667eea', w = 90, h = 32 }) {
  if (!scores || scores.length < 2) return null;
  const pts = scores.slice(-10);
  const minV = Math.min(...pts);
  const maxV = Math.max(...pts);
  const range = maxV - minV || 1;
  const xs = pts.map((_, i) => (i / (pts.length - 1)) * w);
  const ys = pts.map(v => h - ((v - minV) / range) * (h - 4) - 2);
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <polyline points={xs.map((x, i) => `${x},${ys[i]}`).join(' ')}
        fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
      <circle cx={xs[xs.length - 1]} cy={ys[ys.length - 1]} r="3" fill={color} />
    </svg>
  );
}

// ─── Projection: Overview ─────────────────────────────────────────────────────
function OverviewProjection({ logs }) {
  if (!logs.length) return <EmptyState />;

  const totalSessions = logs.length;
  const avgScore = avg(logs.map(l => Number(l.score)));
  const bestScore = best(logs.map(l => Number(l.score)));
  const totalTime = logs.reduce((s, l) => s + (l.timeTaken || 0), 0);
  const completed = logs.filter(l => l.completionStatus === 'completed').length;
  const completionRate = Math.round((completed / totalSessions) * 100);

  // activity breakdown
  const byActivity = {};
  logs.forEach(l => {
    if (!byActivity[l.activityName]) byActivity[l.activityName] = [];
    byActivity[l.activityName].push(Number(l.score));
  });
  const activities = Object.entries(byActivity).sort((a, b) => b[1].length - a[1].length);

  // recent 5
  const recent = logs.slice(0, 5);

  return (
    <div>
      {/* KPI row */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 32 }}>
        <KpiCard icon="🎮" label="Total Sessions" value={totalSessions}
          bg="linear-gradient(135deg,#667eea,#764ba2)" />
        <KpiCard icon="⭐" label="Average Score" value={`${avgScore.toFixed(1)}%`}
          bg="linear-gradient(135deg,#f093fb,#f5576c)" />
        <KpiCard icon="🏆" label="Best Score" value={`${bestScore}%`}
          bg="linear-gradient(135deg,#4facfe,#00f2fe)" />
        <KpiCard icon="⏱️" label="Total Time" value={fmt(totalTime)}
          bg="linear-gradient(135deg,#43e97b,#38f9d7)" />
        <KpiCard icon="✅" label="Completion Rate" value={`${completionRate}%`}
          sub={`${completed} of ${totalSessions} completed`}
          bg="linear-gradient(135deg,#fa709a,#fee140)" />
      </div>

      {/* Activity breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 16, marginBottom: 32 }}>
        {activities.map(([name, scores], i) => {
          const color = ACTIVITY_PALETTE[i % ACTIVITY_PALETTE.length];
          const avgS = avg(scores);
          const trend = scores.length >= 2
            ? (scores[0] > scores[scores.length - 1] ? '📈' : scores[0] < scores[scores.length - 1] ? '📉' : '➡️')
            : '➡️';
          return (
            <div key={name} style={{
              background: '#fff', borderRadius: 16, padding: '18px 20px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.07)',
              borderLeft: `5px solid ${color}`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: '1rem', color: '#2d3436' }}>{name}</div>
                  <div style={{ fontSize: '0.78rem', color: '#636e72', marginTop: 2 }}>{scores.length} session{scores.length !== 1 ? 's' : ''}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 900, color }}>{avgS.toFixed(0)}%</div>
                  <div style={{ fontSize: '0.9rem' }}>{trend}</div>
                </div>
              </div>
              <ScoreBar pct={avgS} color={color} />
              <div style={{ marginTop: 8 }}>
                <Sparkline scores={scores.slice().reverse()} color={color} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent activity */}
      <div style={{ background: '#fff', borderRadius: 16, padding: '20px 24px', boxShadow: '0 4px 16px rgba(0,0,0,0.07)' }}>
        <h3 style={{ margin: '0 0 16px', color: '#2d3436', fontSize: '1.1rem', fontWeight: 800 }}>🕐 Recent Sessions</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                {['Activity', 'Level', 'Score', 'Time', 'Status', 'Date'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#636e72', borderBottom: '2px solid #e9ecef' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recent.map(l => (
                <tr key={l.id} style={{ borderBottom: '1px solid #f1f2f6' }}>
                  <td style={{ padding: '10px 14px', fontWeight: 600 }}>{l.activityName}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ background: LEVEL_COLORS[l.level] || '#dfe6e9', padding: '3px 10px', borderRadius: 99, fontSize: '0.78rem', fontWeight: 700, textTransform: 'capitalize' }}>{l.level}</span>
                  </td>
                  <td style={{ padding: '10px 14px', fontWeight: 700, color: Number(l.score) >= 70 ? '#26de81' : Number(l.score) >= 40 ? '#fd9644' : '#fc5c65' }}>{Number(l.score).toFixed(0)}%</td>
                  <td style={{ padding: '10px 14px', color: '#636e72' }}>{fmt(l.timeTaken)}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ background: STATUS_COLOR[l.completionStatus] || '#dfe6e9', color: '#fff', padding: '3px 10px', borderRadius: 99, fontSize: '0.78rem', fontWeight: 700, textTransform: 'capitalize' }}>{l.completionStatus}</span>
                  </td>
                  <td style={{ padding: '10px 14px', color: '#636e72', whiteSpace: 'nowrap' }}>{fmtDate(l.loggedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Projection: By Activity ──────────────────────────────────────────────────
function ActivityProjection({ logs }) {
  const [sort, setSort] = useState('sessions'); // sessions | avg | best
  if (!logs.length) return <EmptyState />;

  const byActivity = {};
  logs.forEach(l => {
    const n = l.activityName;
    if (!byActivity[n]) byActivity[n] = [];
    byActivity[n].push(l);
  });

  let rows = Object.entries(byActivity).map(([name, items], i) => {
    const scores = items.map(x => Number(x.score));
    const chronoScores = items.slice().reverse().map(x => Number(x.score));
    return {
      name, color: ACTIVITY_PALETTE[i % ACTIVITY_PALETTE.length],
      sessions: items.length,
      avgScore: avg(scores), bestScore: best(scores),
      totalTime: items.reduce((s, x) => s + x.timeTaken, 0),
      lastPlayed: items[0]?.loggedAt,
      chronoScores,
      completedCount: items.filter(x => x.completionStatus === 'completed').length,
    };
  });

  rows = rows.sort((a, b) => sort === 'avg' ? b.avgScore - a.avgScore : sort === 'best' ? b.bestScore - a.bestScore : b.sessions - a.sessions);

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 700, color: '#636e72', lineHeight: '34px' }}>Sort by:</span>
        {[['sessions','# Sessions'],['avg','Avg Score'],['best','Best Score']].map(([k, label]) => (
          <button key={k} onClick={() => setSort(k)} style={{
            padding: '6px 16px', borderRadius: 99, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem',
            background: sort === k ? '#667eea' : '#e9ecef', color: sort === k ? '#fff' : '#636e72',
          }}>{label}</button>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {rows.map(r => (
          <div key={r.name} style={{ background: '#fff', borderRadius: 16, padding: '20px 24px', boxShadow: '0 4px 16px rgba(0,0,0,0.07)', borderLeft: `5px solid ${r.color}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1.15rem', color: '#2d3436' }}>{r.name}</div>
                <div style={{ fontSize: '0.8rem', color: '#636e72', marginTop: 3 }}>
                  {r.sessions} session{r.sessions !== 1 ? 's' : ''} · Last: {fmtDate(r.lastPlayed)} · Total time: {fmt(r.totalTime)}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <Stat label="Avg Score" value={`${r.avgScore.toFixed(1)}%`} color={r.color} />
                <Stat label="Best Score" value={`${r.bestScore}%`} color="#26de81" />
                <Stat label="Completed" value={`${r.completedCount}/${r.sessions}`} color="#fd9644" />
              </div>
            </div>
            <div style={{ marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#636e72', marginBottom: 4 }}>
                <span>Avg score</span><span>{r.avgScore.toFixed(1)}%</span>
              </div>
              <ScoreBar pct={r.avgScore} color={r.color} height={12} />
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#636e72', marginBottom: 4 }}>
                <span>Best score</span><span>{r.bestScore}%</span>
              </div>
              <ScoreBar pct={r.bestScore} color="#26de81" height={12} />
            </div>
            <div style={{ marginTop: 10 }}>
              <Sparkline scores={r.chronoScores} color={r.color} w={120} h={36} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '1.4rem', fontWeight: 900, color }}>{value}</div>
      <div style={{ fontSize: '0.72rem', color: '#636e72' }}>{label}</div>
    </div>
  );
}

// ─── Projection: By Level ─────────────────────────────────────────────────────
function LevelProjection({ logs }) {
  if (!logs.length) return <EmptyState />;

  const byLevel = {};
  logs.forEach(l => {
    const lv = l.level || 'beginner';
    if (!byLevel[lv]) byLevel[lv] = [];
    byLevel[lv].push(l);
  });

  const rows = LEVEL_ORDER
    .filter(lv => byLevel[lv])
    .map(lv => {
      const items = byLevel[lv];
      const scores = items.map(x => Number(x.score));
      const activitiesSet = [...new Set(items.map(x => x.activityName))];
      return {
        level: lv,
        color: LEVEL_COLORS[lv] || '#dfe6e9',
        sessions: items.length,
        avgScore: avg(scores),
        bestScore: best(scores),
        activities: activitiesSet,
        totalTime: items.reduce((s, x) => s + x.timeTaken, 0),
        completedCount: items.filter(x => x.completionStatus === 'completed').length,
      };
    });

  const maxSessions = Math.max(...rows.map(r => r.sessions));

  return (
    <div>
      {/* Radar-style level grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 16, marginBottom: 32 }}>
        {rows.map(r => (
          <div key={r.level} style={{
            background: '#fff', borderRadius: 20, padding: '22px 24px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            borderTop: `6px solid ${r.color}`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <div style={{ textTransform: 'capitalize', fontWeight: 900, fontSize: '1.1rem', color: '#2d3436' }}>{r.level}</div>
                <div style={{ fontSize: '0.78rem', color: '#636e72', marginTop: 2 }}>{r.sessions} session{r.sessions !== 1 ? 's' : ''}</div>
              </div>
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: r.color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1.6rem', fontWeight: 900, color: '#2d3436',
              }}>{r.avgScore.toFixed(0)}%</div>
            </div>

            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: '0.75rem', color: '#636e72', marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
                <span>Avg score</span><span>{r.avgScore.toFixed(1)}%</span>
              </div>
              <ScoreBar pct={r.avgScore} color={r.color} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: '0.75rem', color: '#636e72', marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
                <span>Sessions (relative)</span><span>{r.sessions}</span>
              </div>
              <ScoreBar pct={(r.sessions / maxSessions) * 100} color="#a29bfe" />
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: '0.75rem', color: '#636e72', marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
                <span>Completion</span><span>{Math.round((r.completedCount / r.sessions) * 100)}%</span>
              </div>
              <ScoreBar pct={(r.completedCount / r.sessions) * 100} color="#26de81" />
            </div>

            <div style={{ fontSize: '0.75rem', color: '#636e72', marginTop: 8 }}>
              <span style={{ fontWeight: 700 }}>Activities: </span>
              {r.activities.map(a => (
                <span key={a} style={{ background: '#f1f2f6', padding: '2px 8px', borderRadius: 99, marginRight: 4, display: 'inline-block', marginTop: 3 }}>{a}</span>
              ))}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#636e72', marginTop: 6 }}>
              ⏱️ Total time: {fmt(r.totalTime)} · 🏆 Best: {r.bestScore}%
            </div>
          </div>
        ))}
      </div>

      {/* Comparison table */}
      <div style={{ background: '#fff', borderRadius: 16, padding: '20px 24px', boxShadow: '0 4px 16px rgba(0,0,0,0.07)' }}>
        <h3 style={{ margin: '0 0 16px', color: '#2d3436', fontSize: '1.1rem', fontWeight: 800 }}>📊 Level Comparison</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                {['Level', 'Sessions', 'Avg Score', 'Best Score', 'Completion', 'Total Time', 'Activities'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#636e72', borderBottom: '2px solid #e9ecef' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.level} style={{ borderBottom: '1px solid #f1f2f6' }}>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ background: r.color, padding: '4px 12px', borderRadius: 99, fontWeight: 700, textTransform: 'capitalize', fontSize: '0.85rem' }}>{r.level}</span>
                  </td>
                  <td style={{ padding: '10px 14px', fontWeight: 700 }}>{r.sessions}</td>
                  <td style={{ padding: '10px 14px', fontWeight: 700, color: '#667eea' }}>{r.avgScore.toFixed(1)}%</td>
                  <td style={{ padding: '10px 14px', fontWeight: 700, color: '#26de81' }}>{r.bestScore}%</td>
                  <td style={{ padding: '10px 14px' }}>{Math.round((r.completedCount / r.sessions) * 100)}%</td>
                  <td style={{ padding: '10px 14px', color: '#636e72' }}>{fmt(r.totalTime)}</td>
                  <td style={{ padding: '10px 14px', fontSize: '0.8rem', color: '#636e72' }}>{r.activities.length}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Projection: Timeline ─────────────────────────────────────────────────────
function TimelineProjection({ logs }) {
  const [filterActivity, setFilterActivity] = useState('all');
  const [filterLevel, setFilterLevel] = useState('all');
  if (!logs.length) return <EmptyState />;

  const activityNames = [...new Set(logs.map(l => l.activityName))].sort();

  const filtered = logs.filter(l =>
    (filterActivity === 'all' || l.activityName === filterActivity) &&
    (filterLevel === 'all' || l.level === filterLevel)
  );

  return (
    <div>
      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        <div>
          <label style={{ fontWeight: 700, fontSize: '0.85rem', color: '#636e72', marginRight: 6 }}>Activity:</label>
          <select value={filterActivity} onChange={e => setFilterActivity(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: 8, border: '2px solid #e9ecef', fontWeight: 600, cursor: 'pointer' }}>
            <option value="all">All Activities</option>
            {activityNames.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontWeight: 700, fontSize: '0.85rem', color: '#636e72', marginRight: 6 }}>Level:</label>
          <select value={filterLevel} onChange={e => setFilterLevel(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: 8, border: '2px solid #e9ecef', fontWeight: 600, cursor: 'pointer' }}>
            <option value="all">All Levels</option>
            {LEVEL_ORDER.map(lv => <option key={lv} value={lv} style={{ textTransform: 'capitalize' }}>{lv}</option>)}
          </select>
        </div>
        <span style={{ fontSize: '0.85rem', color: '#636e72', marginLeft: 'auto' }}>{filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Timeline feed */}
      <div style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', left: 19, top: 0, bottom: 0, width: 2, background: '#e9ecef', zIndex: 0 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map((l, i) => {
            const score = Number(l.score);
            const scoreColor = score >= 70 ? '#26de81' : score >= 40 ? '#fd9644' : '#fc5c65';
            const actIdx = activityNames.indexOf(l.activityName);
            const actColor = ACTIVITY_PALETTE[actIdx % ACTIVITY_PALETTE.length];
            return (
              <div key={l.id} style={{ display: 'flex', gap: 16, alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
                {/* Timeline dot */}
                <div style={{
                  width: 40, height: 40, borderRadius: '50%', background: actColor,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.1rem', flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                  border: '3px solid #fff',
                }}>{score >= 70 ? '⭐' : score >= 40 ? '👍' : '💪'}</div>

                {/* Card */}
                <div style={{
                  flex: 1, background: '#fff', borderRadius: 14, padding: '14px 18px',
                  boxShadow: '0 3px 12px rgba(0,0,0,0.07)',
                  borderLeft: `4px solid ${actColor}`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <span style={{ fontWeight: 800, fontSize: '0.98rem', color: '#2d3436' }}>{l.activityName}</span>
                      <span style={{ marginLeft: 8, background: LEVEL_COLORS[l.level] || '#dfe6e9', padding: '2px 10px', borderRadius: 99, fontSize: '0.72rem', fontWeight: 700, textTransform: 'capitalize' }}>{l.level}</span>
                    </div>
                    <span style={{ fontSize: '0.78rem', color: '#b2bec3', whiteSpace: 'nowrap' }}>{fmtDate(l.loggedAt)}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 20, marginTop: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 900, fontSize: '1.2rem', color: scoreColor }}>{score.toFixed(0)}%</span>
                    <span style={{ color: '#636e72', fontSize: '0.85rem', lineHeight: '1.8rem' }}>⏱ {fmt(l.timeTaken)}</span>
                    <span style={{ background: STATUS_COLOR[l.completionStatus] || '#dfe6e9', color: '#fff', padding: '2px 10px', borderRadius: 99, fontSize: '0.72rem', fontWeight: 700, textTransform: 'capitalize', lineHeight: '1.8rem' }}>{l.completionStatus}</span>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <ScoreBar pct={score} color={scoreColor} height={8} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────
function EmptyState() {
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#636e72' }}>
      <div style={{ fontSize: '4rem', marginBottom: 16 }}>📭</div>
      <h3 style={{ fontWeight: 800 }}>No activity data yet</h3>
      <p>Complete some games and your progress will appear here!</p>
    </div>
  );
}

// ─── Dashboard (main export) ──────────────────────────────────────────────────
export default function Dashboard({ onNavigate }) {
  const { isAuthenticated, getToken } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projection, setProjection] = useState('overview');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error('Could not acquire auth token. Please sign in again.');
      const res = await fetch(config.endpoints.myActivities, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to load data (${res.status})`);
      const data = await res.json();
      setLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const current = PROJECTIONS.find(p => p.id === projection);

  return (
    <div className="home-container">
      <Header onNavigate={onNavigate} showHomeButton />
      <section className="hero-section" style={{ paddingBottom: 0 }}>
        <h2 className="hero-title">📊 Activity Dashboard</h2>
        <p className="hero-description">Your learning portfolio across all activities</p>
      </section>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 16px 60px' }}>
        {/* Projection switcher */}
        <div style={{
          display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center',
          margin: '24px 0 32px',
          background: '#fff', borderRadius: 16, padding: '10px 12px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.07)',
        }}>
          {PROJECTIONS.map(p => (
            <button key={p.id} onClick={() => setProjection(p.id)} style={{
              padding: '10px 20px', borderRadius: 12, border: 'none', cursor: 'pointer',
              fontWeight: 700, fontSize: '0.9rem', transition: 'all 0.2s',
              background: projection === p.id ? '#667eea' : 'transparent',
              color: projection === p.id ? '#fff' : '#636e72',
              boxShadow: projection === p.id ? '0 4px 12px rgba(102,126,234,0.4)' : 'none',
            }}>{p.label}</button>
          ))}
        </div>

        {/* Section heading */}
        <h3 style={{ fontWeight: 900, fontSize: '1.3rem', color: '#2d3436', marginBottom: 20 }}>
          {current?.title}
          <button onClick={fetchLogs} title="Refresh" style={{
            marginLeft: 12, background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem',
          }}>🔄</button>
        </h3>

        {!isAuthenticated && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#636e72' }}>
            <div style={{ fontSize: '3rem', marginBottom: 12 }}>🔒</div>
            <p style={{ fontWeight: 700 }}>Sign in to view your dashboard</p>
          </div>
        )}

        {isAuthenticated && loading && (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div className="spinner" />
            <p style={{ color: '#636e72', marginTop: 12 }}>Loading your activity data…</p>
          </div>
        )}

        {isAuthenticated && !loading && error && (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#fc5c65' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>⚠️</div>
            <p style={{ fontWeight: 700 }}>{error}</p>
            <button onClick={fetchLogs} className="start-btn" style={{ marginTop: 12 }}>Try Again</button>
          </div>
        )}

        {isAuthenticated && !loading && !error && (
          <>
            {projection === 'overview'  && <OverviewProjection  logs={logs} />}
            {projection === 'activity'  && <ActivityProjection  logs={logs} />}
            {projection === 'level'     && <LevelProjection     logs={logs} />}
            {projection === 'timeline'  && <TimelineProjection  logs={logs} />}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}
